import { Fragment, useCallback } from 'react';
import { map } from 'lodash';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import type { SortingStrategy } from '@dnd-kit/sortable';

interface SortableItem {
  readonly id: string;
}

interface SortableProps<T, P> {
  readonly items: T;
  readonly itemComponent: (item: P, index: number) => React.ReactNode;
  readonly strategy?: SortingStrategy;
  readonly onDragEnd: (oldId: string, newId: string) => void;
}

export const Sortable = <T extends SortableItem[], P = T[0]>({
  itemComponent, onDragEnd, items,
  strategy = rectSortingStrategy,
}: SortableProps<T, P>) => {
  const handleDrag = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active?.id && over?.id && active.id !== over.id) {
      onDragEnd(active.id as string, over.id as string);
    }
  }, [onDragEnd]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDrag}
    >
      <SortableContext
        items={items}
        strategy={strategy}
      >
        {map(items, (item, index) => (
          <Fragment key={item.id}>
            {itemComponent(item as any, index)}
          </Fragment>
        ))}
      </SortableContext>
    </DndContext>
  );
};
